import { useState, useRef, useEffect } from 'react';
import { FiX, FiSend } from 'react-icons/fi';
import { useChat } from '../../contexts/ChatContext';
import { useMemberAuth } from '../../contexts/MemberAuthContext';
import AttachmentList from './AttachmentList';
import ReactionBar from './ReactionBar';
import Avatar from './Avatar';
import { formatTime } from '../../utils/chatTime';
import { cn } from '../../utils/helpers';

/**
 * Thread view, shown over the message list.
 *
 * Replies are one level deep by design — the server rejects a reply whose parent
 * is itself a reply, so there is no nesting to render.
 */
export default function ThreadPanel() {
  const { thread, closeThread, sendThreadReply, timezone } = useChat();
  const { member } = useMemberAuth();
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [thread?.parent?.id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [thread?.replies?.length]);

  if (!thread) return null;

  const submit = async () => {
    const body = value.trim();
    if (!body || sending) return;

    setSending(true);
    setValue('');
    try {
      await sendThreadReply(body);
    } catch {
      // The failed reply surfaces in the list like any other message.
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const renderMessage = (message, isParent = false) => {
    const isMine = message.sender?.id === member?.id;
    return (
      <div className={cn('flex gap-2', isParent ? 'pb-4 mb-1 border-b border-neutral-300' : 'mt-3')}>
        <Avatar
          name={message.sender?.displayName}
          photoUrl={message.sender?.photoUrl}
          seed={message.sender?.id}
          size="sm"
          className="mt-0.5"
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-serif text-sm font-semibold text-primary">
              {isMine ? 'You' : message.sender?.displayName || 'Unknown member'}
            </span>
            <span className="text-[11px] text-primary/40">{formatTime(message.createdAt, timezone)}</span>
          </div>

          <div
            className={cn(
              'inline-block max-w-full rounded-[10px] px-3.5 py-2',
              // The thread's opening message is the one being answered, so it
              // carries the pale blue the member's own bubbles use.
              isParent
                ? 'bg-highlight text-primary border border-primary/15'
                : 'bg-white text-primary/90 border border-neutral-300'
            )}
          >
            {message.deletedAt ? (
              <p className="text-sm italic text-primary/40">
                This message was deleted.
              </p>
            ) : (
              <p className="text-sm whitespace-pre-wrap break-words">{message.body}</p>
            )}
          </div>

          {!message.deletedAt && message.attachments?.length > 0 && (
            <AttachmentList attachments={message.attachments} />
          )}

          {!message.deletedAt && !message.pending && <ReactionBar messageId={message.id} compact />}
        </div>
      </div>
    );
  };

  return (
    <div className="absolute inset-0 z-10 bg-neutral-50 flex flex-col">
      <header className="flex items-center gap-2 px-4 py-3 bg-neutral-100 border-b border-neutral-300">
        <h3 className="flex-1 font-serif text-base text-primary">Thread</h3>
        <button
          onClick={closeThread}
          className="p-1.5 -mr-1.5 rounded-lg text-secondary hover:text-primary hover:bg-neutral-200 transition-colors"
          aria-label="Close thread"
        >
          <FiX className="w-5 h-5" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-3 bg-neutral-50">
        {renderMessage(thread.parent, true)}

        {thread.loading ? (
          <p className="text-sm text-primary/40 py-4">Loading replies…</p>
        ) : thread.replies.length === 0 ? (
          <p className="text-sm text-primary/40 py-4">No replies yet.</p>
        ) : (
          thread.replies.map((reply) => (
            <div key={reply.id || reply.clientMsgId}>{renderMessage(reply)}</div>
          ))
        )}

        <div ref={bottomRef} />
      </div>

      <div className="border-t border-neutral-300 bg-neutral-100 px-3 py-2.5">
        <div className="flex items-end gap-1 rounded-[10px] bg-white border border-neutral-300 p-1.5
                        focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/40 transition-all">
          <textarea
            ref={inputRef}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            rows={1}
            maxLength={8000}
            placeholder="Reply to thread…"
            className="flex-1 resize-none bg-transparent border-0 px-2 py-2 text-sm leading-5
                       text-primary placeholder:text-primary/35 focus:outline-none focus:ring-0"
          />
          <button
            onClick={submit}
            disabled={!value.trim() || sending}
            className={cn(
              'shrink-0 p-2 rounded-full transition-all',
              value.trim() && !sending
                ? 'bg-accent text-primary shadow-sm hover:bg-accent-600 hover:shadow-md active:scale-95'
                : 'bg-primary/10 text-primary/30 cursor-not-allowed'
            )}
            aria-label="Send reply"
          >
            <FiSend className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
